import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import {
  INVOICE_REGISTRY_OUTCOMES,
  INVOICE_REGISTRY_OUTCOME_LABELS_RU,
  type InvoiceRegistryCandidateDto,
  type InvoiceRegistryOutcome,
  type InvoiceRegistryResultDto,
  type InvoiceRegistryRowResultDto,
} from '@crm/shared'
import { assignInvoiceNumber } from '../../shared/api/bank.api'
import { apiErrorText } from '../../shared/api/errorText'
import { toast } from '../../shared/ui/toastStore'
import { BrokerGrid } from '../customs/broker/BrokerGrid'
import { formatBankDate, formatBankMoney } from './bankFormat'

type Filter = 'ALL' | InvoiceRegistryOutcome

type GridRow = {
  id: string
  number: string
  dateLabel: string
  date: string
  state: string
  buyer: string
  sum: number | null
  outcomeLabel: string
  targetLabel: string
  raw: InvoiceRegistryRowResultDto
}

/** Подпись счёта заказа: заявка, сумма счёта и дата, когда счёт выставлен в микрохабе. */
export function candidateLabel(c: InvoiceRegistryCandidateDto): string {
  return `Заявка ${c.requestNumber ?? '—'} · счёт на ${formatBankMoney(c.totalSum)} от ${formatBankDate(c.issuedAt)}`
}

function toGridRow(r: InvoiceRegistryRowResultDto): GridRow {
  return {
    id: r.number,
    number: r.number,
    dateLabel: formatBankDate(r.date),
    date: r.date ?? '',
    state: r.state ?? '',
    buyer: r.clientName ?? r.buyer,
    sum: r.sum,
    outcomeLabel: INVOICE_REGISTRY_OUTCOME_LABELS_RU[r.outcome],
    targetLabel: r.target ? candidateLabel(r.target) : '',
    raw: r,
  }
}

/** Ячейка выбора счёта заказа: список свободных счетов клиента и кнопка записи номера. */
function AssignCell({
  row,
  onAssign,
}: {
  row: InvoiceRegistryRowResultDto
  onAssign: (row: InvoiceRegistryRowResultDto, invoiceId: string) => Promise<void>
}) {
  const [invoiceId, setInvoiceId] = useState('')
  const [busy, setBusy] = useState(false)
  if (row.candidates.length === 0) {
    return <span className="text-gray-400">свободных счетов не осталось</span>
  }
  return (
    <span className="flex items-center gap-2">
      <select
        value={invoiceId}
        onChange={(e) => setInvoiceId(e.target.value)}
        aria-label={`Счёт заказа для ${row.number}`}
        className="min-w-0 flex-1 rounded-lg border border-gray-300 px-2 py-1 text-sm"
      >
        <option value="">Не записывать</option>
        {row.candidates.map((c) => (
          <option key={c.invoiceId} value={c.invoiceId}>
            {candidateLabel(c)}
          </option>
        ))}
      </select>
      <button
        type="button"
        disabled={!invoiceId || busy}
        onClick={() => {
          setBusy(true)
          void onAssign(row, invoiceId).finally(() => setBusy(false))
        }}
        className="shrink-0 px-2 py-1 bg-brand-600 text-white text-xs rounded-lg hover:bg-brand-700 disabled:opacity-60"
      >
        {busy ? '…' : 'Записать'}
      </button>
    </span>
  )
}

/**
 * Итог загрузки реестра счетов 1С (этап 3, Б4). Плитки сверху — счётчики по итогам;
 * нажатие на плитку показывает её счета в таблице. В таблице — фильтры по столбцам
 * и выбор счёта заказа там, где программа не записала номер сама.
 */
export function BankInvoiceRegistryModal({
  result,
  onClose,
  onChanged,
}: {
  result: InvoiceRegistryResultDto
  onClose: () => void
  /** Номер записан вручную — перечитать очередь и выписки. */
  onChanged: () => void
}) {
  const [rows, setRows] = useState<InvoiceRegistryRowResultDto[]>(result.rows)
  const [filter, setFilter] = useState<Filter>('ALL')

  const counts = useMemo(() => {
    const map = Object.fromEntries(INVOICE_REGISTRY_OUTCOMES.map((o) => [o, 0])) as Record<
      InvoiceRegistryOutcome,
      number
    >
    for (const r of rows) map[r.outcome] += 1
    return map
  }, [rows])
  const shown = useMemo(
    () => rows.filter((r) => filter === 'ALL' || r.outcome === filter).map(toGridRow),
    [rows, filter],
  )

  const assign = async (row: InvoiceRegistryRowResultDto, invoiceId: string) => {
    try {
      const res = await assignInvoiceNumber(invoiceId, row.number)
      const target = row.candidates.find((c) => c.invoiceId === invoiceId) ?? null
      // Строка — «Номер записан»; занятый счёт убираем из выбора у остальных.
      setRows((list) =>
        list.map((r) =>
          r.number === row.number
            ? {
                ...r,
                outcome: 'APPLIED',
                reason: 'Номер записан вручную',
                target,
                candidates: [],
              }
            : { ...r, candidates: r.candidates.filter((c) => c.invoiceId !== invoiceId) },
        ),
      )
      onChanged()
      toast.success(
        `Номер ${row.number} записан в счёт заказа` +
          (res.autoAllocated > 0 ? `; разнесено поступлений: ${res.autoAllocated}` : ''),
      )
    } catch (e) {
      toast.error(apiErrorText(e, 'Не удалось записать номер счёта'))
    }
  }

  const colDefs = useMemo<ColDef<GridRow>[]>(
    () => [
      { field: 'number', headerName: 'Счёт 1С', minWidth: 140 },
      {
        field: 'dateLabel',
        headerName: 'Дата',
        minWidth: 110,
        filterParams: { dateMode: true },
        comparator: (_a, _b, nodeA, nodeB) =>
          (nodeA.data?.date ?? '').localeCompare(nodeB.data?.date ?? ''),
      },
      { field: 'state', headerName: 'Состояние в 1С', minWidth: 150 },
      { field: 'buyer', headerName: 'Покупатель', minWidth: 220 },
      {
        field: 'sum',
        headerName: 'Сумма в 1С',
        minWidth: 140,
        filter: false,
        cellClass: 'text-right tabular-nums',
        headerClass: 'ag-right-aligned-header',
        valueFormatter: ({ value }: { value: number | null }) => formatBankMoney(value),
      },
      {
        field: 'outcomeLabel',
        headerName: 'Итог',
        minWidth: 190,
        cellRenderer: ({ data }: ICellRendererParams<GridRow>) =>
          data ? <span title={data.raw.reason}>{data.outcomeLabel}</span> : null,
      },
      {
        field: 'targetLabel',
        headerName: 'Счёт заказа в микрохабе',
        flex: 1,
        minWidth: 420,
        sortable: false,
        cellRenderer: ({ data }: ICellRendererParams<GridRow>) => {
          if (!data) return null
          const r = data.raw
          if (r.target) {
            return (
              <a
                href={`/orders/${r.target.orderId}`}
                target="_blank"
                rel="noreferrer"
                className="text-brand-600 hover:text-brand-700"
                title="Открыть заказ в новой вкладке"
              >
                {candidateLabel(r.target)} ↗
              </a>
            )
          }
          if (r.outcome === 'CHOOSE' || r.outcome === 'SUM_MISMATCH') {
            return <AssignCell row={r} onAssign={assign} />
          }
          return <span className="text-gray-400">—</span>
        },
      },
    ],
    // assign замыкает только стабильные setRows и onChanged.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const tiles: Array<{ id: Filter; label: string; count: number; hint: string }> = [
    { id: 'ALL', label: 'Счетов в файле', count: rows.length, hint: 'Все счета реестра' },
    ...INVOICE_REGISTRY_OUTCOMES.filter((o) => o !== 'UNKNOWN_ENTITY' || counts[o] > 0).map(
      (o) => ({
        id: o as Filter,
        label: INVOICE_REGISTRY_OUTCOME_LABELS_RU[o],
        count: counts[o],
        hint: 'Показать эти счета',
      }),
    ),
  ]

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-label="Счета 1С"
        className="flex h-[90vh] w-full max-w-7xl flex-col rounded-xl border border-gray-200 bg-white shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-800">Счета 1С загружены</h3>
          <button
            onClick={onClose}
            aria-label="Закрыть"
            className="text-lg leading-none text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-3 p-4">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
            {tiles.map((t) => (
              <button
                key={t.id}
                type="button"
                title={t.hint}
                aria-pressed={filter === t.id}
                onClick={() => setFilter(t.id)}
                className={`rounded-xl border p-3 text-left transition-colors ${
                  filter === t.id
                    ? 'border-brand-600 bg-brand-50'
                    : 'border-gray-200 bg-white hover:border-brand-500'
                }`}
              >
                <div className="text-lg font-semibold tabular-nums text-gray-900">{t.count}</div>
                <div className="text-xs text-gray-500">{t.label}</div>
              </button>
            ))}
          </div>

          <p className="text-xs text-gray-500">
            Нажмите плитку, чтобы увидеть её счета. «Сумма не совпала» — у клиента есть счета
            заказов без номера, но на другие суммы: скорее всего, это другой заказ, такие строки
            можно пропустить. «Заказа в микрохабе нет» — заказ не завели или счёт не выгрузили.
            {result.autoAllocated > 0
              ? ` После записи номеров разнеслось поступлений из очереди: ${result.autoAllocated}.`
              : ''}
          </p>

          <BrokerGrid<GridRow>
            rowData={shown}
            columnDefs={colDefs}
            storageKey="crm_bank_registry_cols_v1"
            className="min-h-0 flex-1"
            noRowsText="Счетов с таким итогом нет"
          />
        </div>

        <div className="flex justify-end border-t border-gray-100 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
